import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IAgentIdInLocalStorage, ICommonPaymentData, ICommonPaymentDataWithFullName } from '../../interfaces';
import { LoggerService } from '../logger/logger.service';
import { EXPIRY_DAYS_FOR_AGENTID } from 'src/app/app.constants';
import { ActivatedRoute } from '@angular/router';
import { filter, map, retry, takeUntil } from 'rxjs/operators';
import { IBaseClient } from 'src/app/client-id/base-client/interface';
import { CreditCardService } from '../creditCardService/credit-card.service';
import { MessageService } from '../message/message.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ClientId } from 'src/app/client-id/client-id';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  loading$ = new BehaviorSubject<boolean>(true);
  loadingApi$ = new BehaviorSubject<string|null>('Payment Details'); // null, cardData: "Payment Details", agentList: "Agent Details"
  agentId$ = new BehaviorSubject<string | null>(null);
  agentList$ = new BehaviorSubject<string[]>([]);

  refId$ = new BehaviorSubject<string | null>(null);
  captureByLinkRefId$ = new BehaviorSubject<string | null>(null);
  private privateCardData$ = new BehaviorSubject<ICommonPaymentData | null>(null);
  transferred$ = new BehaviorSubject<boolean | null>(null);
  termsAndConditionsRead$ = new BehaviorSubject<boolean>(false);
  isCardOnFileSelected$ = new BehaviorSubject<boolean>(false);
  ccaasMasking$ = new BehaviorSubject<boolean>(false);
  clientId$ = new BehaviorSubject<IBaseClient | null>(null);

  originalCardData: ICommonPaymentData | null = null;
  agentListWatcher = new Subject<void>();

  constructor(
    private loggerSrv: LoggerService,
    private active: ActivatedRoute,
    private creditCardSrv: CreditCardService,
    private messageSrv: MessageService,
    private http: HttpClient,
    private clientId: ClientId,
  ) {
    this.setRefIdFromQueryParams();

    this.creditCardSrv.getCreditCardDetailsNew(this.refId$.getValue() || '').subscribe({
      next: (cardData) => {
        //cardData
        if (!cardData?.commonPaymentData) {
          this.privateCardData$.next(null);
          this.loggerSrv.log('No Card details available for this reference Id.');
          return;
        }
        this.privateCardData$.next(cardData.commonPaymentData);

        //identify clientId
        const clientId = this.clientId.getClient(this.active.snapshot.queryParams, cardData.commonPaymentData);
        this.clientId$.next(clientId);

        // make API call to fetch agents list if needed
        if (clientId.needsAgentList) {
          this.loadingApi$.next('Agent Details');
          this.getAgentsList().subscribe({
            next: (agents) => {
              // agents
              if (!agents?.agentids.length) {
                this.loggerSrv.log('No agents found in LifeRay.');
                return;

              }
              this.agentList$.next(agents.agentids.map((id: string) => id.toLowerCase()));
              this.loading$.next(false);
              this.loadingApi$.next(null);
            },
            error: (err) => {
              this.loggerSrv.log(`Error fetching agents list from LifeRay: ${err}`);
              this.loading$.next(false);
              this.loadingApi$.next(null);
            }
          });
        } else {
          this.loading$.next(false);
          this.loadingApi$.next(null);
        }
      },
      error: (err) => {
        // cardData - error
        this.loading$.next(false);
        const message = err.error?.errorMessage ? err.error.errorMessage : 'Error occurred while fetching credit card details. Please try again.';
        this.messageSrv.showMessage(message, 'error');
      }
    });

    this.setAgentIdAndList();
    this.setCblStatus();
  }

  get cardData$(): Observable<ICommonPaymentDataWithFullName | null> {
    return this.privateCardData$.asObservable().pipe(
      filter((data) => !!data),
      map(data => {
        if (!data) {
          this.loggerSrv.log('No card data available.');
          return null;
        }

        return {
          ...data,
          fullName: `${data.firstName} ${data.lastName}`,
          cardLastFour: data.cardLastFour || ''
        };
      })
    );
  }

  getAgentsList(): Observable<{ agentids: string[] }> {
    return this.http.get<{ agentids: string[] }>(`${environment.webContentDomain}cinch-apis/-/webcontent/article/COMMON-PAYMENT-AGENTIDS?uniqId=${new Date().getTime()}`).pipe(
      retry(1),
    );
  }

  doesAgentHaveCxOneId(agent:string):boolean {
    if (agent) {
      const list = this.agentList$.getValue();
      return list.find(item => item.split(':')[0] === agent)?.split(':')[1] ? true : false;
    }
    // if not an agent, then customer is using form.
    return true;
  }


  setRefIdFromQueryParams(): void {
    const params = this.active.snapshot.queryParams;
    const refId = params['refId'];

    if (refId) {
      this.refId$.next(refId);
      this.loggerSrv.log(`RefId set from query params: ${refId}`);
    } else {
      this.loggerSrv.log('No RefId found in query params.');
    }
  }

  setCardData$(data:ICommonPaymentData | null): void {
    this.privateCardData$.next(data);
  }

  private setCblStatus(): void {
    const cblRef = this.active.snapshot.queryParams['cblRefId'];
    this.captureByLinkRefId$.next(cblRef || null);
    this.transferred$.next(!!cblRef);
  }

  private setAgentIdAndList() {
    const agentIdParam = this.active.snapshot.queryParams['agentId'];
    if (agentIdParam) {
      this.agentId$.next(agentIdParam);
      this.agentList$.pipe(takeUntil(this.agentListWatcher)).subscribe({
        next: (list) => {
          if (list.length) {
            const found = list.find((item) => item.split(':')[0] === agentIdParam);
            if (found) {
              this.agentId$.next(found);
              this.setAgentIdWithExpiry(found);
            } else {
              this.loggerSrv.log('***** AGENT ID - Agent Id by URL is not found on list.');
              localStorage.removeItem('agentIdWithPhone');
            }
            this.agentListWatcher.next();
            this.agentListWatcher.complete();
          }
        }
      });
    } else {
      const agentId:string|null = this.extractAgentIdFromStorage();
      (agentId) && this.agentId$.next(agentId);
    }
  }

  getAgentIdFromList(agentId:string): string[] | null {
    const agentList = this.agentList$.getValue() || [];
    const found = agentList.find((item) => item?.split(':')[0] === agentId);
    if (found) {
      return found.split(":");
    }

    this.loggerSrv.log('***** AGENT ID - Agent Id is not found on list.');
    return null;
  }

  extractAgentIdFromStorage(): string | null {
    const id:string|null = localStorage.getItem('agentIdWithPhone');
    if (id) {
      try {
        const parsedId:IAgentIdInLocalStorage = JSON.parse(id);
        if (parsedId.expiry > new Date().valueOf()) {
          return parsedId.value;
        } else {
          localStorage.removeItem('agentIdWithPhone');
        }
      } catch (error) {
        console.warn('Failed to parse agentIdWithPhone from localStorage:', error);
        localStorage.removeItem('agentIdWithPhone');
      }
    }
    return null;
  }

  saveOriginalCardData(data: ICommonPaymentData | null): void {
    this.originalCardData = data;
  }

  useOriginalCardData(): void {
    this.setCardData$(this.originalCardData);
  }

  redirectToApplication(url: string | null): void {
    if (!url || url === '') {
      this.loggerSrv.log('No redirect url available.');
      return;
    }

    window.location.href = url;
  }

  setAgentIdWithExpiry(value: string | null): void {
    if (!value) { return; }

    const now = new Date();
    // expiry set for 30 days
    const item:IAgentIdInLocalStorage = {
      value: value,
      expiry: now.valueOf() + (EXPIRY_DAYS_FOR_AGENTID * 86400000),
    }

    localStorage.setItem('agentIdWithPhone', JSON.stringify(item));
    this.agentId$.next(value);
  }
}
